import React, { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import ApprovalItemList from "./reusable/approval-item-list.jsx";
import FormsSearch from "./reusable/forms-search.jsx";
import "./request-main.css";
import FormsDropdown from "./reusable/forms-search-dropdown.jsx";

function ApprovalRequests() {
  const PAGE_SIZE = 8;

  const [formData, setFormData] = useState([]);
  const [isInitialLoading, setIsInitialLoading] =
    useState(true);
  const [isFetchingMore, setIsFetchingMore] =
    useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [formsList, setFormsList] = useState([]);
  const [selectedForm, setSelectedForm] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const debounceRef = useRef(null);

  /*
   * Fetch CCAR details because the approval-list API
   * does not contain warehouse and reference_number.
   */
  const enrichRequestsWithCcarDetails = async (
    requests = [],
  ) => {
    const enrichedRequests = await Promise.all(
      requests.map(async (item) => {
        const isCcar =
          item?.request_type?.toUpperCase() === "CCAR";

        if (!isCcar || !item?.id) {
          return item;
        }

        try {
          /*
           * This is the same API method used by
           * your view-request-detail component.
           */
          const detailResponse =
            await ApiService.forms.getFormRequestDetail(
              item.request_type,
              item.id,
            );

          console.log(
            "CCAR DETAIL RESPONSE:",
            detailResponse,
          );

          if (!detailResponse?.success) {
            return item;
          }

          /*
           * Your screenshot shows:
           *
           * detailResponse.request.warehouse
           * detailResponse.request.reference_number
           *
           * The additional fallbacks support a nested response
           * in case the backend changes the structure.
           */
          const responseRequest =
            detailResponse?.request || null;

          const ccarRequest =
            responseRequest?.warehouse !== undefined ||
            responseRequest?.reference_number !== undefined
              ? responseRequest
              : responseRequest?.requisition
                  ?.form_request_detail ||
                responseRequest?.form_request_detail ||
                null;

          return {
            ...item,
            ccar_request: ccarRequest,
          };
        } catch (error) {
          console.error(
            `Failed to fetch CCAR details for ${item.id}:`,
            error,
          );

          return item;
        }
      }),
    );

    return enrichedRequests;
  };

  const getForms = async ({
    page = 1,
    pageSize = PAGE_SIZE,
    keyword = "",
    append = false,
  } = {}) => {
    try {
      if (append) {
        setIsFetchingMore(true);
      } else {
        setIsInitialLoading(true);
      }

      const response =
        await ApiService.forms.getApprovalRequestsListV2(
          page,
          pageSize,
          keyword,
          selectedForm,
        );

      console.log("APPROVAL RESPONSE:", response);

      const { success, requests = [] } =
        response || {};

      console.log("APPROVAL REQUESTS:", requests);

      if (!success) {
        showSwal(
          common.errors.GETTING_APPROVAL_LIST_ERROR,
        );

        setHasMore(false);
        return;
      }

      /*
       * Load warehouse and reference number
       * for all CCAR requests in the current page.
       */
      const enrichedRequests =
        await enrichRequestsWithCcarDetails(requests);

      console.log(
        "ENRICHED APPROVAL REQUESTS:",
        enrichedRequests,
      );

      setFormData((previousData) => {
        if (!append) {
          return enrichedRequests;
        }

        const existingIds = new Set(
          previousData.map((item) => item?.id),
        );

        const dedupedNewItems =
          enrichedRequests.filter(
            (item) =>
              !existingIds.has(item?.id),
          );

        return [
          ...previousData,
          ...dedupedNewItems,
        ];
      });

      setHasMore(requests.length === pageSize);

      setPagination((previousPagination) => ({
        ...previousPagination,
        page,
        pageSize,
      }));
    } catch (error) {
      console.error(
        "Failed to fetch approval requests:",
        error,
      );

      showSwal(
        common.errors.GETTING_APPROVAL_LIST_ERROR,
      );

      setHasMore(false);
    } finally {
      setIsInitialLoading(false);
      setIsFetchingMore(false);
    }
  };

  const getFormsList = async () => {
    try {
      const response =
        await ApiService.forms.getForms();

      const { success, forms = [] } =
        response || {};

      if (success) {
        const formattedForms = forms.map(
          (form) => ({
            label: form.name,
            value: form.id,
          }),
        );

        setFormsList(formattedForms);
      } else {
        showSwal(
          common.errors.GETTING_FORMS_LIST_ERROR,
        );
      }
    } catch (error) {
      console.error(
        "Failed to fetch forms list:",
        error,
      );
    }
  };

  useEffect(() => {
    getForms({
      page: 1,
      pageSize: PAGE_SIZE,
      keyword: "",
      append: false,
    });

    getFormsList();
  }, []);

  useEffect(() => {
    setFormData([]);
    setHasMore(true);

    getForms({
      page: 1,
      pageSize: PAGE_SIZE,
      keyword: searchKeyword,
      append: false,
    });
  }, [selectedForm]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const fetchMoreData = async () => {
    if (
      isFetchingMore ||
      !hasMore ||
      isInitialLoading
    ) {
      return;
    }

    await getForms({
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
      keyword: searchKeyword,
      append: true,
    });
  };

  const handleSearch = (query) => {
    const trimmedQuery = query.trim();

    setSearchKeyword(trimmedQuery);
    setHasMore(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setFormData([]);

      getForms({
        page: 1,
        pageSize: PAGE_SIZE,
        keyword: trimmedQuery,
        append: false,
      });
    }, 400);
  };

  return (
    <div className="request-main">
      <div className="request-main-title">
        <div className="request-main-title__top">
          <h3>My Pending Approvals</h3>
        </div>

        <div className="request-main-title__search">
          <FormsSearch
            handleSearch={handleSearch}
          />

          <FormsDropdown
            value={selectedForm}
            options={formsList}
            handleSelect={(value) =>
              setSelectedForm(value)
            }
          />
        </div>
      </div>

      {isInitialLoading ? (
        <div className="loader--container">
          <div className="loader--"></div>
        </div>
      ) : (
        <div
          id="approval-requests-scrollable-container"
          className="request-main---container"
        >
          <InfiniteScroll
            dataLength={formData.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <div className="request-main__bottom-loader">
                <div className="loader-- small"></div>

                <span>
                  Loading more approvals...
                </span>
              </div>
            }
            endMessage={
              formData.length > 0 ? (
                <p className="request-main__end-message">
                  You’ve reached the end.
                </p>
              ) : null
            }
            scrollableTarget="approval-requests-scrollable-container"
            scrollThreshold="180px"
          >
            {formData.length > 0 ? (
              <div className="request-main__list">
                {formData.map((data) => (
                  <div
                    className="request-main__item"
                    key={data?.id}
                  >
                    <ApprovalItemList
                      data={data}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="request-main__empty-state">
                <div className="request-main__empty-icon">
                  ✅
                </div>

                <h4>
                  No pending approvals found
                </h4>

                <p>
                  {searchKeyword
                    ? "Try changing your search keyword."
                    : "You currently have no pending approvals."}
                </p>
              </div>
            )}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
}

export default ApprovalRequests;